import { t } from 'i18next';

import type { HttpRequestError } from 'common/services';
import { AppConfigDaoService } from 'features/app-config/services';
import type { CallCenterCodeSource } from 'features/machine-preferences/helpers';

export interface InactiveCallCenterDialogCopy {
  title: string;
  message: string;
}

export type InactiveCallCenterReason = 'missing' | 'inactiveOrInvalid';

export class InactiveOrInvalidCallCenterError extends Error {
  readonly source: CallCenterCodeSource;
  readonly callCenterCode: string;
  readonly reason: InactiveCallCenterReason;

  constructor({
    source,
    callCenterCode,
    reason = 'inactiveOrInvalid',
  }: {
    source: CallCenterCodeSource;
    callCenterCode: string;
    reason?: InactiveCallCenterReason;
  }) {
    super(
      reason === 'missing'
        ? 'Home call center is missing'
        : source === 'url'
          ? 'Requested call center is inactive or invalid'
          : 'Home call center is inactive or invalid'
    );
    this.name = 'InactiveOrInvalidCallCenterError';
    this.source = source;
    this.callCenterCode = callCenterCode;
    this.reason = reason;
  }
}

/** Network/5xx/unexpected failures — callers should allow retry. */
export class CallCenterValidationTransientError extends Error {
  constructor(message = 'Call center validation temporarily unavailable') {
    super(message);
    this.name = 'CallCenterValidationTransientError';
  }
}

const HTTP_ERROR_BODY_MARKER = 'Message:';
const HTTP_ERROR_STATUS_PATTERN = /Status:\s*(\d+)\./;
const CALL_CENTER_INVALID_CODE = 'CALL_CENTER_INVALID';

const normalizeError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

const isInactiveOrInvalidCallCenter = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const { isCallCenterActive, validationState } = payload as {
    isCallCenterActive?: unknown;
    validationState?: unknown;
  };

  return isCallCenterActive === false || validationState === 'invalid';
};

const parseHttpErrorBody = (error: unknown): unknown => {
  const message = normalizeError(error).message;
  const markerIndex = message.indexOf(HTTP_ERROR_BODY_MARKER);

  if (markerIndex === -1) {
    return null;
  }

  const body = message
    .slice(markerIndex + HTTP_ERROR_BODY_MARKER.length)
    .trim();

  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
};

/**
 * HttpService sets `error.status`; the message pattern is a fallback for
 * errors that only carry the formatted message.
 */
const getHttpErrorStatus = (error: unknown): number | null => {
  const status = (error as HttpRequestError | null)?.status;

  if (typeof status === 'number') {
    return status;
  }

  const match = HTTP_ERROR_STATUS_PATTERN.exec(normalizeError(error).message);

  return match ? Number(match[1]) : null;
};

/**
 * Maps a createSession rejection (HTTP 400, body `code: CALL_CENTER_INVALID`)
 * to the user-visible missing call center error. Returns null for any other
 * failure so callers keep their generic handling.
 */
export const mapCallCenterInvalidResponse = (
  error: unknown,
  source: CallCenterCodeSource
): InactiveOrInvalidCallCenterError | null => {
  const status = getHttpErrorStatus(error);
  const body = parseHttpErrorBody(error);

  if (
    status !== 400 ||
    !body ||
    typeof body !== 'object' ||
    (body as { code?: unknown }).code !== CALL_CENTER_INVALID_CODE
  ) {
    return null;
  }

  return new InactiveOrInvalidCallCenterError({
    source,
    callCenterCode: '',
    reason: 'missing',
  });
};

/**
 * Confirms the resolved call center is active.
 * - inactive / invalid → InactiveOrInvalidCallCenterError (terminal for that code)
 * - other transport failures → CallCenterValidationTransientError (retryable)
 */
export const assertCallCenterIsActive = async ({
  callCenterCode,
  source,
}: {
  callCenterCode: string;
  source: CallCenterCodeSource;
}): Promise<void> => {
  try {
    const response =
      await AppConfigDaoService.isCallCenterActive(callCenterCode);

    if (
      !response.isCallCenterActive ||
      response.validationState === 'invalid'
    ) {
      throw new InactiveOrInvalidCallCenterError({
        source,
        callCenterCode,
      });
    }
  } catch (error) {
    if (error instanceof InactiveOrInvalidCallCenterError) {
      throw error;
    }

    if (isInactiveOrInvalidCallCenter(parseHttpErrorBody(error))) {
      throw new InactiveOrInvalidCallCenterError({
        source,
        callCenterCode,
      });
    }

    throw new CallCenterValidationTransientError(normalizeError(error).message);
  }
};

export const getInactiveCallCenterErrorCopy = (
  error: InactiveOrInvalidCallCenterError
): InactiveCallCenterDialogCopy => {
  const errorType = getInactiveCallCenterErrorType(error);

  return {
    title: t(`machinePreferences.callCenter.${errorType}.title`),
    message: t(`machinePreferences.callCenter.${errorType}.message`, {
      callCenterCode: error.callCenterCode,
    }),
  };
};

const getInactiveCallCenterErrorType = (
  error: InactiveOrInvalidCallCenterError
) => {
  switch (error.reason) {
    case 'missing':
      return 'homeCallCenterMissing';
    case 'inactiveOrInvalid':
      return error.source === 'url'
        ? 'shortcutCallCenterInactiveOrInvalid'
        : 'homeCallCenterInactiveOrInvalid';
    default: {
      const exhaustive: never = error.reason;
      return exhaustive;
    }
  }
};
